import { useState, useEffect } from 'react';
import shallow from 'zustand/shallow';
import { useSelector } from 'react-redux';

import { MODAL_TYPE } from 'global/TradingModal/RealTradingModal/types';

import { ORDER_TYPE, STOCK_TYPE } from 'constants/trading';

import If from 'global/ErrorHandlers/If';

import AutoOrderTncModal from 'global/TradingModal/RealTradingModal/TransactionModal/AutoOrderTncModal';
import {
  clevertapPortfolioDetailExercise,
  clevertapPortfolioDetailViewed,
  clevertapPortfolioSell,
} from 'lib/analytics/securities';
import { clevertapEvent } from 'utils/clevertap';
import { VIRTUAL_TRADING_TYPES } from 'lib/analytics/virtualTrading';
import useTradingAccount from 'hooks/useTradingAccount';

import tradingDataStore from 'lib/stores/tradingData';
import tradingModalStore from 'global/TradingModal/store';
import { selectors as profileSelectors } from 'lib/entities/profile';
import usePortfolioDetailStore from 'global/Securities/stores/usePortfolioDetailStore';
import useEmittenInfoStore from 'global/Securities/stores/useEmittenInfoStore';
import { StockType } from 'global/Securities/types/stockType';
import { AutoOrderProduct } from 'global/Securities/types/autoOrder';
import type { PortfolioStateItem } from 'global/Securities/types/store/portfolio';
import { withMaintenanceGuard } from 'global/Securities/utils/maintenance';
import useGlobalModalStore from 'global/GlobalModalManager/stores/useGlobalModalStore';

import { useBondPortfolioStore } from 'global/TradingModal/RealTradingModal/ModalBonds/stores/useBondPortfolioStore';
import { useBondsModalStore } from 'global/TradingModal/RealTradingModal/ModalBonds/stores/useBondsModalStore';
import useBondsAnalytics from 'global/TradingModal/RealTradingModal/ModalBonds/useBondsAnalytics';
import { formatNumber } from 'utils/number';
import { BondsModalTypeEnum } from 'global/TradingModal/RealTradingModal/ModalBonds/constants';
import ModalBondCouponReceipt from 'global/TradingModal/RealTradingModal/ModalBonds/components/ModalBondCouponReceipt';
import TradingContextProvider from 'features/transaction/provider';
import { SellOrderType } from 'global/Securities/types/transaction';
import AutoOrder from '../AutoOrder';
import DetailModal from '../DetailModal';
import SymbolHistoryModal from '../History/SymbolHistoryModal';
import ModalDetail from './ModalDetail';
import ModalDetailStockBond from './ModalDetailStockBond';
import BondModalDetail from './BondModalDetail';
import { ModalButtonContainerSecondary } from '../styled';

import { iModal } from '../types';
import { DetailOrderButton } from '../OrderTable/styled';

interface PortoModal extends iModal {
  symbol: string;
  isDayTrade: boolean;
  exerciseable: boolean;
  type: string;
  initialModalType?: string;
  initialAutoOrderType?: SellOrderType;
}

const MODAL_TYPES = {
  detail: 'detail',
  exercise: ORDER_TYPE.EXERCISE,
  sell: ORDER_TYPE.SELL,
  history: 'history',
  tnc: MODAL_TYPE.AUTOBUY_TNC,
  autoorder: 'auto-order',
  bondCouponHistory: BondsModalTypeEnum.COUPON_RECEIPT,
};

export const PortoDetailModal = ({
  visible,
  symbol,
  isDayTrade,
  exerciseable,
  type,
  onCancel,
  initialModalType = MODAL_TYPES.detail,
  initialAutoOrderType,
}: PortoModal) => {
  const { isVirtualTrading } = useTradingAccount();

  const { allowOrder, portfolioType, canSell } = tradingDataStore((s) => {
    const selectedData = isDayTrade
      ? s.portfolio.data?.day_trade
      : s.portfolio.data?.result;

    const portfolioItem: Partial<PortfolioStateItem> =
      selectedData?.find((item) => item.symbol === symbol) ?? {};

    return {
      allowOrder: portfolioItem.allow_order,
      portfolioType: portfolioItem.type,
      canSell: portfolioItem?.can_sell,
    };
  }, shallow);

  const { tradingModalVisible, showTradingModal } = tradingModalStore(
    (s) => ({
      tradingModalVisible: s.visible,
      showTradingModal: s.showModal,
    }),
    shallow,
  );

  const globalActiveModal = useGlobalModalStore((s) => s.activeModal);

  const { portfolioDetail, portfolioDetailDayTrade, loadPortfolioDetail } =
    usePortfolioDetailStore(
      (s) => ({
        portfolioDetail: s.data,
        portfolioDetailDayTrade: s.dayTrade,
        isLoading: s.isLoading,
        loadPortfolioDetail: s.loadPortfolioDetail,
      }),
      shallow,
    );

  const { typeCompany, orderbook } = useEmittenInfoStore(
    (s) => ({
      typeCompany: s.data.type_company?.toLowerCase() as StockType,
      orderbook: s.data.orderbook,
    }),
    shallow,
  );

  const {
    data: { username },
  } = useSelector(profileSelectors);

  const [modalType, setModalType] = useState(initialModalType);

  const selectedPortfolioDetail = isDayTrade
    ? portfolioDetailDayTrade
    : portfolioDetail;

  const {
    available_lot,
    unrealised_gain,
    symbol: detailSymbol,
    company_name: companyName,
  } = selectedPortfolioDetail ?? {};

  const isAllowOrder = allowOrder === 1;
  const isKEF01CB = symbol === 'KAEF01CB';
  const isBond = type === STOCK_TYPE.bond;

  const [bondDetails, bondProduct] = useBondPortfolioStore((s) => [
    s.detailBySymbol,
    s.product,
  ]);

  const openBondSellModal = useBondsModalStore((s) => s.openSellModal);
  const { FRBondActionTrackers } = useBondsAnalytics();

  const handleTradeExerciseClick = () =>
    withMaintenanceGuard(() => {
      setModalType(ORDER_TYPE.EXERCISE);
      clevertapPortfolioDetailExercise(symbol);
    });

  const handleTradeSellClick = () =>
    withMaintenanceGuard(() => {
      setModalType(ORDER_TYPE.SELL);

      const TYPE = isVirtualTrading
        ? VIRTUAL_TRADING_TYPES.virtualTrading
        : VIRTUAL_TRADING_TYPES.realTrading;

      clevertapEvent('Sell Order Viewed', {
        Symbol: symbol,
        Type: TYPE,
      });

      clevertapPortfolioSell({
        tradingType: TYPE,
        symbol,
        source: 'Portfolio Stock Detail Page',
        gainLoss: parseFloat(String(unrealised_gain)).toFixed(4),
      });
    });

  const handleTradeBondSellClick = () => {
    openBondSellModal(symbol, undefined, 'Portfolio Detail');
    FRBondActionTrackers.onOrderButtonClicked(
      'Sell',
      symbol,
      'Portfolio Detail',
    );
    onCancel();
  };

  useEffect(() => {
    if (
      [ORDER_TYPE.EXERCISE, ORDER_TYPE.SELL].includes(modalType) &&
      orderbook
    ) {
      showTradingModal({
        symbol,
        orderType: modalType,
        orderedLot: Math.floor(available_lot),
      });
    }
  }, [modalType]);

  useEffect(() => {
    if (!tradingModalVisible && !globalActiveModal) {
      setModalType(MODAL_TYPES.detail);
    }
  }, [tradingModalVisible, globalActiveModal]);

  useEffect(() => {
    if (unrealised_gain && symbol === detailSymbol) {
      clevertapPortfolioDetailViewed({
        symbol,
        gainLoss: unrealised_gain * 100,
        exerciseable,
        dayTrade: isDayTrade,
      });
    }
  }, [unrealised_gain, detailSymbol]);

  useEffect(() => {
    if (!globalActiveModal) loadPortfolioDetail(symbol).catch(onCancel);
  }, [symbol, globalActiveModal]);

  useEffect(() => {
    setModalType(initialModalType);
  }, [initialModalType]);

  const footerButton = (
    <ModalButtonContainerSecondary>
      <If condition={exerciseable}>
        <DetailOrderButton
          variant="amend"
          className="exercise"
          onClick={handleTradeExerciseClick}
        >
          Exercise
        </DetailOrderButton>
      </If>
      <If condition={isAllowOrder && canSell && !isBond}>
        <DetailOrderButton
          variant="cancel"
          disabled={!canSell}
          onClick={handleTradeSellClick}
        >
          Sell
        </DetailOrderButton>
      </If>
      <If condition={isBond && bondDetails?.invested_amount > 0}>
        <DetailOrderButton
          variant="cancel"
          disabled={Number(bondDetails?.units ?? 0) === 0}
          onClick={handleTradeBondSellClick}
          onClickCapture={() => {
            FRBondActionTrackers.onOrderButtonClicked(
              'Sell',
              symbol,
              'Portfolio Page',
              formatNumber(bondDetails?.yield_rate, false, '0,0.00'),
            );
          }}
        >
          Sell
        </DetailOrderButton>
      </If>
    </ModalButtonContainerSecondary>
  );

  return (
    <>
      <If condition={modalType === MODAL_TYPES.bondCouponHistory}>
        <ModalBondCouponReceipt
          isVisible={modalType === MODAL_TYPES.bondCouponHistory}
          onCancel={() => setModalType(MODAL_TYPES.detail)}
        />
      </If>
      <If condition={modalType === MODAL_TYPES.detail}>
        <DetailModal
          visible={visible}
          onCancel={onCancel}
          title="Portfolio Detail"
          symbol={symbol}
          showPrice={false}
          isBonds={isBond}
          bondsName={bondProduct?.label ?? companyName}
          showBondPrice={!isKEF01CB}
          suffix={footerButton}
        >
          <If condition={isBond && !isKEF01CB}>
            <BondModalDetail
              symbol={symbol}
              onCouponHistoryClick={() => {
                setModalType(MODAL_TYPES.bondCouponHistory);
              }}
            />
          </If>

          <If condition={isBond && isKEF01CB}>
            <ModalDetailStockBond symbol={symbol} />
          </If>

          <If condition={!isBond}>
            <ModalDetail
              symbol={symbol}
              username={username}
              stockType={typeCompany}
              isDayTrade={isDayTrade}
              onAutoOrderClick={setModalType}
            />
          </If>
        </DetailModal>
      </If>

      <If condition={modalType === MODAL_TYPES.history}>
        <SymbolHistoryModal
          visible={modalType === MODAL_TYPES.history}
          symbol={symbol}
          stockType={portfolioType ?? type}
          onCancel={onCancel}
        />
      </If>

      <TradingContextProvider>
        <If condition={modalType === MODAL_TYPES.autoorder}>
          <AutoOrder
            visible={modalType === MODAL_TYPES.autoorder}
            symbol={symbol}
            onCancel={onCancel}
            initialType={initialAutoOrderType}
          />
        </If>

        <If condition={modalType === MODAL_TYPES.tnc}>
          <AutoOrderTncModal
            autoOrderType={AutoOrderProduct.AutoSell}
            visible={modalType === MODAL_TYPES.tnc}
            onAccept={() => setModalType(MODAL_TYPES.autoorder)}
            closeModal={() => setModalType(MODAL_TYPES.detail)}
          />
        </If>
      </TradingContextProvider>
    </>
  );
};

export default PortoDetailModal;
